/* eslint-disable no-restricted-globals */
/// <reference lib="webworker" />

import { ref as storageRef, StorageReference, UploadMetadata, StorageError, TaskEvent, TaskState, ListResult, FirebaseStorage, StorageObserver, UploadTaskSnapshot } from 'firebase/storage';
import { Unsubscribe } from '@reduxjs/toolkit';
import { Queue } from 'workbox-background-sync';
import { CloudResponse, EmptyResponse, SuccesfulResponse } from '../../sync/CloudResponse';
import { ProjectFileMetaData } from '../../../shared/sync/dataTypes/ProjectFileMetaData';
import { FirebaseWrapper } from '../FirebaseBaseWrapper';
import { getFirebaseConfig } from '../firebase-config';
import { UrlHelpers } from '@/shared/services/UrlHelpers';


export type AwaitableStorageTask = StorageTask & {
  /** resolves on success, rejects on error/cancel */
  done: Promise<UploadTaskSnapshot>;
};

const firebaseConfig = getFirebaseConfig();
const auth = FirebaseWrapper.FirebaseBaseWrapper.shared.firebaseAuth;


async function getIdToken(): Promise<string | null> {
  return await auth.currentUser?.getIdToken();
}

// Queue used to replay failed upload requests when connectivity returns. This relies
// on the Background Sync API so uploads can continue even if the page is closed
// while the service worker remains active.
//
// The Queue constructor expects to run in a Service Worker global scope where
// `self.registration` is defined. When this module is imported outside of a
// service worker (e.g. in tests or other runtime environments), `self` refers
// to `window` and has no `registration` property, which causes an error inside
// Workbox when it checks for background sync support. Guard the creation so the
// queue is only instantiated when a registration is available.
const uploadQueue = typeof self !== 'undefined' && 'registration' in self && (self as any).registration
  ? new Queue('firebase-upload-queue')
  : undefined;

// Helper to convert different observer forms
function toObserver<S>(
  nextOrObserver?: StorageObserver<S> | null | ((s: S) => unknown),
  error?: ((a: StorageError) => unknown) | null,
  complete?: Unsubscribe | null
): StorageObserver<S> {
  if (typeof nextOrObserver === 'function') {
    return { next: nextOrObserver, error: error ?? undefined, complete: complete ?? undefined };
  }
  return {
    next: nextOrObserver?.next,
    error: nextOrObserver?.error ?? error ?? undefined,
    complete: nextOrObserver?.complete ?? complete ?? undefined,
  };
}

export interface DownloadTaskSnapshotSW {
  bytesTransferred: number;
  totalBytes: number;
  state: TaskState;            // 'running' | 'success' | 'error' | 'canceled' etc.
  ref: StorageReference;
  contentType?: string | null;
  blob?: Blob | null;          // set only on success
}

export interface StorageTask {
  cancel(): boolean;
  on(
    event: TaskEvent,
    nextOrObserver?: StorageObserver<UploadTaskSnapshot> | null | ((snapshot: UploadTaskSnapshot) => unknown),
    error?: ((a: StorageError) => unknown) | null,
    complete?: Unsubscribe | null
  ): Unsubscribe;
}

async function tryReadError(res: Response): Promise<string | null> {
  try { return JSON.stringify(await res.clone().json()); } catch {}
  try { return await res.text(); } catch {}
  return null;
}

export async function listAll(storageReference: StorageReference): Promise<ListResult> {
  const token = await getIdToken();
  if (!token) {
    throw new Error('listAllSW: No authenticated user.');
  }
  const bucket = firebaseConfig.storageBucket;
  const baseUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o`;

  // Firebase "folder" semantics are prefix-based; ensure trailing slash for non-root
  const rawPrefix = storageReference.fullPath.replace(/^\/+|\/+$/g, '');
  const prefix = rawPrefix ? `${rawPrefix}/` : '';

  const headers = { Authorization: `Bearer ${token}` };

  const prefixes: StorageReference[] = [];
  const items: StorageReference[] = [];

  let pageToken: string | undefined;

  do {
    const qs = new URLSearchParams();
    if (prefix) qs.set('prefix', prefix);
    qs.set('delimiter', '/');
    if (pageToken) qs.set('pageToken', pageToken);

    const res = await fetch(`${baseUrl}?${qs.toString()}`, { headers });
    if (!res.ok) {
      const msg = await tryReadError(res);
      throw new Error(`listAllSW: HTTP ${res.status} ${msg ?? ''}`.trim());
    }

    const json = await res.json();
    for (const p of (json.prefixes ?? []) as string[]) {
      const childPath = p.replace(/\/$/, '');
      prefixes.push(storageRef(storageReference.storage as FirebaseStorage, childPath));
    }

    for (const obj of (json.items ?? []) as Array<{ name: string }>) {
      items.push(storageRef(storageReference.storage as FirebaseStorage, obj.name));
    }

    pageToken = json.nextPageToken;
  } while (pageToken);

  return { prefixes, items };
}

export async function putJson(
  storageRef: StorageReference,
  jsonObject: object,
  metadata?: UploadMetadata,
  completion?: (snapshot: UploadTaskSnapshot | null, error: Error | null) => void
): Promise<StorageTask> {
  const jsonBlob = new Blob([JSON.stringify(jsonObject)], { type: 'application/json' });
  return putFileBlob(storageRef, jsonBlob, metadata, completion);
}

export async function putFile(
  storageRef: StorageReference,
  fileUrl: string,
  metadata?: UploadMetadata,
  completion?: (snapshot: UploadTaskSnapshot | null, error: Error | null) => void
): Promise<AwaitableStorageTask> {
  try {
    const blob = await urlToBlob(fileUrl);
    return putFileBlob(storageRef, blob, metadata, completion);
  } catch (error) {
    console.error(`Failed to read url ${fileUrl} with error: ${error}`);
    completion?.(null, error as Error);
    return { cancel() { return false; }, on() { return () => {}; } } as unknown as AwaitableStorageTask;
  }
}

export async function putFileBlob(
  storageRef: StorageReference,
  blob: Blob,
  metadata?: UploadMetadata,
  completion?: (snapshot: UploadTaskSnapshot | null, error: Error | null) => void
): Promise<AwaitableStorageTask> {
  let observers: StorageObserver<UploadTaskSnapshot>[] = [];
  const notifyNext  = (s: UploadTaskSnapshot) => observers.forEach(o => o.next?.(s));
  const notifyError = (e: StorageError)        => observers.forEach(o => o.error?.(e));
  const notifyDone  = ()                      => observers.forEach(o => o.complete?.());

  const aborter = new AbortController();
  let finished = false;
  let canceled = false;

  const mkSnap = (bytesTransferred: number, totalBytes: number, state: TaskState): UploadTaskSnapshot => ({
    bytesTransferred,
    totalBytes,
    state,
    ref: storageRef,
    metadata: undefined,
    task: undefined,
  } as unknown as UploadTaskSnapshot);

  let resolveDone!: (s: UploadTaskSnapshot) => void;
  let rejectDone!: (e: Error) => void;
  const done = new Promise<UploadTaskSnapshot>((res, rej) => {
    resolveDone = res;
    rejectDone  = rej;
  });

  const task: AwaitableStorageTask = {
    cancel(): boolean {
      if (finished || canceled) return false;
      canceled = true;
      aborter.abort();
      queueMicrotask(() => notifyNext(mkSnap(0, 0, 'canceled')));
      try { rejectDone(new Error('Upload canceled.')); } catch {}
      return true;
    },
    on(
      event: TaskEvent,
      nextOrObserver?: StorageObserver<UploadTaskSnapshot> | null | ((snapshot: UploadTaskSnapshot) => unknown),
      error?: ((a: StorageError) => unknown) | null,
      complete?: Unsubscribe | null
    ): Unsubscribe {
      if (event !== 'state_changed') return () => {};
      const obs = toObserver<UploadTaskSnapshot>(nextOrObserver, error ?? undefined, complete ?? undefined);
      observers.push(obs);
      return () => { observers = observers.filter(o => o !== obs); };
    },
    done,
  };

  // Fire-and-forget async upload logic.
  void (async () => {
    try {
      const authToken = await getIdToken();
      if (!authToken) throw new Error('No authenticated user.');

      const encodedPath = encodeURIComponent(storageRef.fullPath);
      const uploadUrl =
        `https://firebasestorage.googleapis.com/v0/b/${firebaseConfig.storageBucket}/o?name=${encodedPath}&uploadType=media`;
      const totalBytes = blob.size;

      const request = new Request(uploadUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': metadata?.contentType || blob.type || 'application/octet-stream',
        },
        body: blob,
        signal: aborter.signal,
      } as unknown as RequestInit);

      // Signal start
      queueMicrotask(() => {
        if (!canceled) notifyNext(mkSnap(0, totalBytes, 'running'));
      });

      // Helper: should we queue this response for retry?
      const isRetryableStatus = (status: number) =>
        status === 408 || status === 429 || (status >= 500 && status <= 599);

      let res: Response;

      // 1) Try the network first. Queue ONLY if needed.
      try {
        res = await fetch(request.clone());
      } catch (err: any) {
        // Abort/cancel is not a queue case
        if (canceled || err?.name === 'AbortError') throw err;

        if (uploadQueue) {
          await uploadQueue.pushRequest({ request /* , event: fetchEvent if you have it */ });

          finished = true;
          const queuedSnap = mkSnap(0, totalBytes, 'paused'); // "queued for replay"
          queueMicrotask(() => {
            if (!canceled) notifyNext(queuedSnap);
          });
          notifyDone();
          resolveDone(queuedSnap);
          completion?.(queuedSnap, null);
          return;
        }

        throw err;
      }

      // 2) If we got an HTTP response, decide whether to queue or fail.
      if (!res.ok) {
        if (uploadQueue && isRetryableStatus(res.status)) {
          await uploadQueue.pushRequest({ request /* , event if available */ });

          finished = true;
          const queuedSnap = mkSnap(0, totalBytes, 'paused');
          queueMicrotask(() => {
            if (!canceled) notifyNext(queuedSnap);
          });
          notifyDone();
          resolveDone(queuedSnap);
          completion?.(queuedSnap, null);
          return;
        }

        const msg = await tryReadError(res);
        throw new Error(`Upload failed with status ${res.status}${msg ? `: ${msg}` : ''}`);
      }

      // 3) Success now (no queue involved).
      finished = true;
      const successSnap = mkSnap(totalBytes, totalBytes, 'success');
      queueMicrotask(() => {
        if (!canceled) notifyNext(successSnap);
      });
      notifyDone();
      resolveDone(successSnap);
      completion?.(successSnap, null);
    } catch (err: any) {
      if (canceled || err?.name === 'AbortError') {
        const se: StorageError = {
          name: 'FirebaseError',
          code: 'storage/canceled',
          message: 'Upload canceled.',
        } as StorageError;

        notifyError(se);
        completion?.(null, new Error(se.message));
        rejectDone(new Error(se.message));
      } else {
        const msg = String(err?.message || err);
        const se: StorageError = {
          name: 'FirebaseError',
          code: 'storage/unknown',
          message: msg,
        } as StorageError;

        notifyError(se);
        completion?.(null, new Error(msg));
        rejectDone(new Error(msg));
      }
    }
  })();

  return task;
}


export function downloadFile(
  storageRef: StorageReference,
  fileURL: string,
  completion?: (fileURL: string | null, error: Error | null) => void
): StorageTask {
  let observers: StorageObserver<DownloadTaskSnapshotSW>[] = [];
  const notifyNext   = (s: DownloadTaskSnapshotSW) => observers.forEach(o => o.next?.(s));
  const notifyError  = (e: StorageError)              => observers.forEach(o => o.error?.(e));
  const notifyDone   = ()                             => observers.forEach(o => o.complete?.());

  const aborter = new AbortController();
  let finished = false;
  let canceled = false;

  const mkSnap = (
    bytesTransferred: number,
    totalBytes: number,
    state: TaskState,
    blob?: Blob | null,
    contentType?: string | null,
  ): DownloadTaskSnapshotSW => ({
    bytesTransferred,
    totalBytes,
    state,
    ref: storageRef,
    blob,
    contentType,
  });

  (async () => {
    try {
      const authToken = await getIdToken();
      if (!authToken) {
        notifyError({ name: 'FirebaseError', code: 'storage/unauthenticated', message: 'No authenticated user.' } as StorageError);
        completion?.(null, new Error('No authenticated user.'));
        return;
      }
      const bucket = firebaseConfig.storageBucket;
      const encodedPath = encodeURIComponent(storageRef.fullPath);
      const baseUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}`;

      let tokenUrl: string | undefined;
      const metadataRes = await fetch(`${baseUrl}?alt=json`, {
        headers: { Authorization: `Bearer ${authToken}` },
        signal: aborter.signal,
      });
      if (!metadataRes.ok) {
        const payload = await safeJson(metadataRes);
        throw new Error(`Metadata fetch failed with status ${metadataRes.status}: ${JSON.stringify(payload)}`);
      }
      const meta = await metadataRes.json();
      const expectedTotal = Number(meta.size) || 0;
      tokenUrl = meta.downloadTokens ? `${baseUrl}?alt=media&token=${meta.downloadTokens}` : undefined;
      const contentType = meta.contentType as string | undefined;

      const streamDownload = async (url: string, headers?: Record<string, string>) => {
        const res = await fetch(url, { headers, signal: aborter.signal });
        if (!res.ok) {
          const payload = await safeJson(res);
          throw new Error(`Download failed with status ${res.status}: ${JSON.stringify(payload)}`);
        }
        const reader = res.body?.getReader();
        if (!reader) throw new Error('No reader available');
        let received = 0;
        const chunks: Uint8Array[] = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          if (value) {
            chunks.push(value);
            received += value.byteLength;
            if (!canceled) notifyNext(mkSnap(received, expectedTotal, 'running', null, contentType));
          }
        }
        const blob = new Blob(chunks, { type: contentType ?? res.headers.get('content-type') ?? 'application/octet-stream' });
        return blob;
      };

      let blob: Blob | null = null;
      try {
        if (tokenUrl) {
          blob = await streamDownload(tokenUrl);
        } else {
          const mediaUrl = `${baseUrl}?alt=media`;
          blob = await streamDownload(mediaUrl, { Authorization: `Bearer ${authToken}` });
        }
      } catch (e) {
        if (fileURL) {
          blob = await streamDownload(fileURL);
        } else {
          throw e;
        }
      }

      finished = true;
      const objUrl = await UrlHelpers.createObjectURLSmart(blob!);

      notifyNext(mkSnap(blob!.size, blob!.size, 'success', blob!, contentType));
      notifyDone();
      completion?.(objUrl, null);
    } catch (err: any) {
      const isAbort = canceled || err?.name === 'AbortError';
      const code = isAbort ? 'storage/canceled' : 'storage/unknown';
      const se: StorageError = { name: 'FirebaseError', code, message: String(err?.message || err) } as StorageError;
      notifyError(se);
      completion?.(null, new Error(se.message));
    }
  })();



  async function safeJson(res: Response) {
    try { return await res.clone().json(); } catch {}
    try { return await res.text(); } catch {}
    return null;
  }

  return {
    cancel(): boolean {
      if (finished || canceled) return false;
      canceled = true;
      aborter.abort();
      notifyNext(mkSnap(0, 0, 'canceled', null, null));
      return true;
    },
    on(
      event: TaskEvent,
      nextOrObserver?: StorageObserver<DownloadTaskSnapshotSW> | null | ((snapshot: DownloadTaskSnapshotSW) => unknown),
      error?: ((a: StorageError) => unknown) | null,
      complete?: Unsubscribe | null
    ): Unsubscribe {
      if (event !== 'state_changed') return () => {};
      const obs = toObserver<DownloadTaskSnapshotSW>(nextOrObserver, error ?? undefined, complete ?? undefined);
      observers.push(obs);
      return () => { observers = observers.filter(o => o !== obs); };
    },
  };
}

export async function getMetadata(ref: StorageReference): Promise<any> {
  if (!auth?.currentUser) {
    throw new Error('getMetadataSW: No authenticated user.');
  }
  const token = await getIdToken();
  const bucket = firebaseConfig.storageBucket;
  const encodedPath = encodeURIComponent(ref.fullPath);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const msg = await tryReadError(res);
    const err: any = new Error(msg ?? '');
    err.code = res.status === 404 ? 'storage/object-not-found' : 'storage/unknown';
    throw err;
  }
  return res.json();
}

export async function updateMetadata(ref: StorageReference, metadata: any): Promise<any> {
  if (!auth?.currentUser) {
    throw new Error('updateMetadataSW: No authenticated user.');
  }
  const token = await getIdToken();
  const bucket = firebaseConfig.storageBucket;
  const encodedPath = encodeURIComponent(ref.fullPath);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ metadata }),
  });
  if (!res.ok) {
    const msg = await tryReadError(res);
    throw new Error(`updateMetadataSW: HTTP ${res.status} ${msg ?? ''}`.trim());
  }
  return res.json();
}

export async function exist(ref: StorageReference): Promise<boolean> {
  try {
    await getMetadata(ref);
    return true;
  } catch (error: any) {
    if (error.code === 'storage/object-not-found') {
      return false;
    }
    throw error;
  }
}

export async function deleteFile(stroageReference: StorageReference, filePath?: string): Promise<void> {
  if (!auth?.currentUser) {
    throw new Error('deleteFileSW: No authenticated user.');
  }
  const token = await getIdToken();
  const path = filePath ? storageRef(stroageReference, filePath).fullPath : stroageReference.fullPath;
  const bucket = firebaseConfig.storageBucket;
  const encodedPath = encodeURIComponent(path);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}`;
  const res = await fetch(url, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const msg = await tryReadError(res);
    throw new Error(`deleteFileSW: HTTP ${res.status} ${msg ?? ''}`.trim());
  }
}

export async function deleteAllFiles(ref: StorageReference): Promise<void> {
  const { items, prefixes } = await listAll(ref);
  await Promise.all(items.map(item => deleteFile(item)));
  for (const folder of prefixes) {
    await deleteAllFiles(folder);
  }
}

export async function get(baseURL: StorageReference, maxSize?: number): Promise<ArrayBuffer> {
  if (!auth?.currentUser) {
    throw new Error('getSW: No authenticated user.');
  }
  const token = await getIdToken();
  const bucket = firebaseConfig.storageBucket;
  const encodedPath = encodeURIComponent(baseURL.fullPath);
  const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodedPath}?alt=media`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const msg = await tryReadError(res);
    throw new Error(`getSW: HTTP ${res.status} ${msg ?? ''}`.trim());
  }
  const data = await res.arrayBuffer();
  if (maxSize && data.byteLength > maxSize) {
    throw new Error(`Download failed: file size exceeds max size of ${maxSize} bytes`);
  }
  return data;
}

export async function projectFileMetaData(
  cloudUrl: StorageReference
): Promise<CloudResponse<ProjectFileMetaData> | EmptyResponse> {
  try {
    const cloudMetadata = await getMetadata(cloudUrl);
    const cloudDate = cloudMetadata.timeCreated;
    if (!cloudDate) {
      return EmptyResponse.sendError('sync: missing creation date of cloud file');
    }
    const size = cloudMetadata.size > 0 ? cloudMetadata.size : null;
    const metaData = new ProjectFileMetaData(
      cloudUrl.fullPath,
      new Date(cloudDate),
      size,
      null,
    );
    return new SuccesfulResponse(metaData);
  } catch (error) {
    return EmptyResponse.sendError(`sync: couldn't receive cloud file metadata: ${error}`);
  }
}

async function urlToBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch the file. Status: ${response.status}`);
  }
  return await response.blob();
}
